import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { toast } from 'sonner';
import { RecipeEditor } from './RecipeEditor.js';
import { EndpointTestPanel } from './EndpointTestPanel.js';

interface CanarySummary {
  success: boolean | null;
  zeroRecordEndpoints: string[];
  transformErrorEndpoints: Array<{ name: string; errors: number }>;
  hasCanary: boolean;
}

// We avoid pulling in a YAML parser just for this warning. The recipe YAML is
// produced by our own writer and follows a stable shape — a targeted regex
// parser is sufficient. The full YAML is still rendered verbatim for review.
function summarizeCanary(yamlText: string): CanarySummary {
  const summary: CanarySummary = {
    success: null,
    zeroRecordEndpoints: [],
    transformErrorEndpoints: [],
    hasCanary: false,
  };
  const canaryMatch = /\ncanary:\s*\n([\s\S]*?)(?:\n[a-z][a-zA-Z]*:|\n*$)/.exec(`\n${yamlText}`);
  if (!canaryMatch) return summary;
  summary.hasCanary = true;
  const block = canaryMatch[1] ?? '';
  const successMatch = /(?:^|\n)\s*success:\s*(true|false)/.exec(block);
  if (successMatch) summary.success = successMatch[1] === 'true';
  const entryRe = /-\s*name:\s*([^\n]+)\s*\n\s*records:\s*(\d+)\s*\n\s*transformErrors:\s*(\d+)/g;
  let m: RegExpExecArray | null;
  while ((m = entryRe.exec(block)) !== null) {
    const name = (m[1] ?? '').trim();
    const records = Number.parseInt(m[2] ?? '0', 10);
    const transformErrors = Number.parseInt(m[3] ?? '0', 10);
    if (records === 0) summary.zeroRecordEndpoints.push(name);
    if (transformErrors > 0) summary.transformErrorEndpoints.push({ name, errors: transformErrors });
  }
  return summary;
}

export function RecipeViewer({
  yaml,
  sessionId,
  onAccepted,
  onRejected,
}: {
  yaml: string;
  sessionId: string;
  onAccepted: () => void;
  onRejected: () => void;
}) {
  const qc = useQueryClient();
  // Local working copy of the YAML — starts from the prop, can be mutated by
  // editor + AI-repair patches before being saved to the server.
  const [currentYaml, setCurrentYaml] = useState(yaml);
  const [editMode, setEditMode] = useState(false);

  // Keep currentYaml in sync with the prop when it changes externally
  // (e.g. after a save -> refetch returns the new yaml). Effect-based sync
  // avoids the warning from doing setState during render.
  useEffect(() => {
    if (!editMode) setCurrentYaml(yaml);
  }, [yaml, editMode]);

  const canary = useMemo(() => summarizeCanary(currentYaml), [currentYaml]);
  const showWarning = canary.hasCanary
    && (canary.success === false || canary.zeroRecordEndpoints.length > 0 || canary.transformErrorEndpoints.length > 0);

  const accept = useMutation({
    mutationFn: () => api.acceptScope(sessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['session', sessionId] });
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      toast.success('Recipe accepted — session is ready to start');
      onAccepted();
    },
    onError: (err) => toast.error(`Accept failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  const reject = useMutation({
    mutationFn: () => api.rejectScope(sessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['session', sessionId] });
      toast.info('Recipe rejected — session returned to draft');
      onRejected();
    },
    onError: (err) => toast.error(`Reject failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  if (editMode) {
    return (
      <div className="space-y-2">
        <RecipeEditor
          initialYaml={currentYaml}
          sessionId={sessionId}
          onSaved={(newYaml) => {
            setCurrentYaml(newYaml);
            setEditMode(false);
          }}
          onCancel={() => setEditMode(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {showWarning && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{
            background: 'rgba(251,146,60,0.10)',
            border: '1px solid rgba(251,146,60,0.45)',
            color: 'rgb(253,186,116)',
          }}
        >
          <div className="font-semibold mb-1">⚠ Canary failed — accepting may produce broken imports</div>
          <ul className="text-xs space-y-0.5 ml-1 list-disc list-inside opacity-90">
            {canary.zeroRecordEndpoints.length > 0 && (
              <li>
                Zero records on {canary.zeroRecordEndpoints.length} endpoint(s):{' '}
                <span className="font-mono">{canary.zeroRecordEndpoints.join(', ')}</span>
              </li>
            )}
            {canary.transformErrorEndpoints.length > 0 && (
              <li>
                Transform errors:{' '}
                <span className="font-mono">
                  {canary.transformErrorEndpoints.map((e) => `${e.name} (${e.errors})`).join(', ')}
                </span>
              </li>
            )}
            {canary.success === false && canary.zeroRecordEndpoints.length === 0 && canary.transformErrorEndpoints.length === 0 && (
              <li>Canary reported success=false (see recipe YAML for details).</li>
            )}
          </ul>
          <div className="text-xs mt-2 opacity-80">
            Use the Test / AI Repair buttons below to fix endpoints, or click Edit to hand-edit the YAML.
          </div>
        </div>
      )}

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--glass-border)' }}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-border">
          <div className="text-xs font-semibold uppercase tracking-widest text-text-dimmed">Generated Recipe</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditMode(true)}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity"
              style={{
                background: 'rgba(56,189,248,0.18)',
                color: 'rgb(125,211,252)',
                border: '1px solid rgba(56,189,248,0.45)',
              }}
            >
              ✎ Edit
            </button>
            <button
              type="button"
              onClick={() => reject.mutate()}
              disabled={reject.isPending || accept.isPending}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity disabled:opacity-50"
              style={{ background: 'var(--accent-red)22', color: 'var(--accent-red)', border: '1px solid var(--accent-red)55' }}
            >
              ✗ Reject
            </button>
            <button
              type="button"
              onClick={() => accept.mutate()}
              disabled={accept.isPending || reject.isPending}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity disabled:opacity-50"
              style={{ background: 'var(--accent-green)22', color: 'var(--accent-green)', border: '1px solid var(--accent-green)55' }}
            >
              ✓ Accept Recipe
            </button>
          </div>
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto', background: 'var(--bg-primary)' }} className="p-4">
          <pre className="font-mono text-xs text-text-secondary whitespace-pre-wrap break-words">{currentYaml}</pre>
        </div>
      </div>

      <EndpointTestPanel
        sessionId={sessionId}
        recipeYaml={currentYaml}
        onPatchProposed={(newYaml) => {
          setCurrentYaml(newYaml);
          setEditMode(true);
        }}
      />
    </div>
  );
}
